import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle as AlertTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Crown, Circle, MessageSquare, Edit3, Trash2, Plus, UserPlus, UserMinus, Shield } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ClansPage() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [clans, setClans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClan, setSelectedClan] = useState(null);
  const [clanMembers, setClanMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', tag: '', description: '', discord: '', clan_info: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', tag: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');

  useEffect(() => { fetchClans(); }, []);

  const fetchClans = async () => {
    try {
      const { data } = await axios.get(`${API}/clans`);
      setClans(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openClanDetail = async (clan) => {
    setSelectedClan(clan);
    setMembersLoading(true);
    setClanMembers([]);
    try {
      const { data } = await axios.get(`${API}/clans/${clan.id}/members`);
      setClanMembers(data.members);
      setSelectedClan(data.clan);
    } catch (err) { console.error(err); }
    finally { setMembersLoading(false); }
  };

  const openEditInfo = () => {
    if (!selectedClan) return;
    setEditForm({ name: selectedClan.name || '', tag: selectedClan.tag || '', description: selectedClan.description || '', discord: selectedClan.discord || '', clan_info: selectedClan.clan_info || '' });
    setEditOpen(true);
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    try {
      const payload = isAdmin ? editForm : { description: editForm.description, discord: editForm.discord, clan_info: editForm.clan_info };
      const { data } = await axios.put(`${API}/clans/${selectedClan.id}/info`, payload);
      toast.success('Clan info updated!');
      setSelectedClan(data);
      setEditOpen(false);
      fetchClans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const isClanLeader = (clan) => user?.id === clan?.leader_id;
  const canEdit = (clan) => isClanLeader(clan) || isAdmin;

  const handleDeleteClan = async (clanId) => {
    try {
      await axios.delete(`${API}/clans/${clanId}`);
      toast.success('Clan removed');
      setSelectedClan(null);
      setConfirmDelete(null);
      fetchClans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove clan'); }
  };

  const handleCreateClan = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/clans`, createForm);
      toast.success('Clan submitted! Awaiting admin approval.');
      setCreateOpen(false);
      setCreateForm({ name: '', tag: '', description: '' });
      fetchClans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create clan'); }
    finally { setCreating(false); }
  };

  const openAddPlayer = async () => {
    setAddPlayerOpen(true);
    setPlayerSearch('');
    try { const { data } = await axios.get(`${API}/players`); setAllPlayers(data); }
    catch (err) { console.error('Failed to load players:', err); setAllPlayers([]); }
  };

  const handleAddPlayer = async (playerId) => {
    try {
      const { data } = await axios.post(`${API}/clans/${selectedClan.id}/roster/add`, { user_id: playerId });
      toast.success('Player added!');
      setSelectedClan(data);
      setAddPlayerOpen(false);
      openClanDetail(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add player'); }
  };

  const handleRemovePlayer = async (playerId) => {
    try {
      const { data } = await axios.post(`${API}/clans/${selectedClan.id}/roster/remove`, { user_id: playerId });
      toast.success('Player removed');
      setSelectedClan(data);
      openClanDetail(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove player'); }
  };

  const handleAssignRole = async (playerId, title) => {
    try {
      const { data } = await axios.put(`${API}/clans/${selectedClan.id}/role`, { user_id: playerId, title });
      toast.success(`Title updated to ${title}!`);
      setSelectedClan(data);
      openClanDetail(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign title'); }
  };

  const userHasClan = clans.some(c => c.leader_id === user?.id);
  const filteredAddPlayers = allPlayers.filter(p => p.id !== user?.id && !clanMembers.some(m => m.id === p.id) && p.name.toLowerCase().includes(playerSearch.toLowerCase()));

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="clans-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-16">
          <div className="text-center flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">Rosters & Info</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">Clans</h1>
            <p className="text-zinc-400 max-w-2xl mx-auto">View clan rosters, details, and Discord communities</p>
          </div>
          {isAuthenticated && !userHasClan && (
            <Button onClick={() => setCreateOpen(true)} className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none flex-shrink-0" data-testid="create-clan-button">
              <Plus className="w-4 h-4 mr-2" />Create Clan
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : clans.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Clans Yet</h2>
            <p className="text-zinc-500 mb-6">Be the first to create a clan!</p>
            {isAuthenticated && !userHasClan && (
              <Button onClick={() => setCreateOpen(true)} className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none">Create Clan</Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {clans.map((clan) => (
              <div key={clan.id} className="bg-[#121212] border border-white/5 p-4 card-hover cursor-pointer" onClick={() => openClanDetail(clan)} data-testid={`clan-card-${clan.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    <span className="font-mono text-sm text-cyan-400 font-bold">{clan.tag}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-bold text-white truncate">{clan.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-zinc-500"><Crown className="w-3 h-3 text-yellow-500" />{clan.leader_name}</div>
                  </div>
                  <div className="text-zinc-600 text-xs"><Users className="w-3 h-3 inline mr-1" />{clan.members?.length || 1}</div>
                </div>
                {clan.description && <p className="text-xs text-zinc-500 line-clamp-1">{clan.description}</p>}
                {clan.badges && clan.badges.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    {clan.badges.slice(0, 5).map((b, idx) => (
                      b.image_url ? (
                        <div key={`${idx}-${b.design_id}`} className="flex flex-col items-center">
                          <img src={`${BASE}${b.image_url}`} alt={b.name || b.tier} className="w-7 h-7 object-contain" />
                          <span className="text-[8px] text-cyan-400 font-bold truncate max-w-[40px]">{b.name}</span>
                        </div>
                      ) : null
                    ))}
                    {clan.badges.length > 5 && <span className="text-zinc-600 text-[10px]">+{clan.badges.length - 5}</span>}
                  </div>
                )}
                {clan.discord && <div className="flex items-center gap-1 mt-2 text-xs text-indigo-400"><MessageSquare className="w-3 h-3" /><span className="truncate">{clan.discord}</span></div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Clan */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="create-clan-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-white uppercase">Create Clan</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateClan} className="space-y-4 mt-4">
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Name</Label><Input value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} placeholder="e.g., Shadow Wolves" required className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400" data-testid="clan-name-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Tag (up to 10 chars)</Label><Input value={createForm.tag} onChange={(e) => setCreateForm({...createForm, tag: e.target.value.slice(0, 10)})} placeholder="e.g., SHW" required maxLength={10} minLength={2} className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 font-mono" data-testid="clan-tag-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label><Textarea value={createForm.description} onChange={(e) => setCreateForm({...createForm, description: e.target.value})} placeholder="About your clan..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-desc-input" /></div>
            <Button type="submit" disabled={creating} className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="submit-clan-button">{creating ? 'Creating...' : 'Create Clan'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clan Detail */}
      <Dialog open={!!selectedClan && !editOpen && !addPlayerOpen} onOpenChange={() => setSelectedClan(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-lg" data-testid="clan-detail-dialog">
          {selectedClan && (<>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><span className="font-mono text-xs text-cyan-400 font-bold">{selectedClan.tag}</span></div>
                {selectedClan.name}
              </DialogTitle>
            </DialogHeader>
            {selectedClan.description && <p className="text-zinc-400 text-sm mt-2">{selectedClan.description}</p>}
            {selectedClan.clan_info && <div className="mt-3 p-3 bg-black border border-zinc-800"><p className="text-zinc-300 text-sm whitespace-pre-wrap">{selectedClan.clan_info}</p></div>}
            {selectedClan.discord && <div className="flex items-center gap-2 mt-3 text-indigo-400"><MessageSquare className="w-5 h-5" /><span className="text-sm">{selectedClan.discord}</span></div>}
            {/* Clan Badges */}
            {selectedClan.badges && selectedClan.badges.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <p className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3">Clan Awards</p>
                <div className="flex flex-wrap gap-3">
                  {selectedClan.badges.map((b, idx) => (
                    b.image_url ? (
                      <div key={`${idx}-${b.design_id}`} className="flex flex-col items-center bg-black border border-zinc-800 px-3 py-2 min-w-[72px]">
                        <img src={`${BASE}${b.image_url}`} alt={b.name || b.tier} className="w-12 h-12 object-contain" />
                        <span className="text-[10px] text-cyan-400 mt-1 text-center font-bold">{b.name}</span>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            )}
            {canEdit(selectedClan) && (
              <div className="flex flex-wrap gap-3 mt-3">
                <Button onClick={openEditInfo} variant="outline" className="border-zinc-800 text-zinc-400 hover:text-cyan-400 hover:border-cyan-400 rounded-none" data-testid="edit-clan-info-button"><Edit3 className="w-4 h-4 mr-2" />Edit Clan Info</Button>
                {isAdmin && <Button onClick={() => { setSelectedClan(null); setConfirmDelete(selectedClan); }} variant="outline" className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none" data-testid="remove-clan-button"><Trash2 className="w-4 h-4 mr-2" />Remove Clan</Button>}
              </div>
            )}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">Roster ({clanMembers.length})</h3>
                {isClanLeader(selectedClan) && <Button onClick={openAddPlayer} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none h-7 text-xs" data-testid="add-player-button"><UserPlus className="w-3 h-3 mr-1" />Add Player</Button>}
              </div>
              {membersLoading ? <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div> : (
                <div className="space-y-2">
                  {clanMembers.map((member) => (
                    <div key={member.id} className={`flex items-center gap-2 p-2 ${member.role === 'leader' || member.role === 'co-leader' ? 'bg-yellow-500/5 border border-yellow-500/20' : member.role === 'officer' ? 'bg-cyan-400/5 border border-cyan-400/20' : 'bg-black border border-zinc-800'}`} data-testid={`roster-member-${member.id}`}>
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${member.role === 'leader' || member.role === 'co-leader' ? 'bg-yellow-500/10 border border-yellow-500/30' : member.role === 'officer' ? 'bg-cyan-400/10 border border-cyan-400/30' : 'bg-zinc-800 border border-zinc-700'}`}>
                        <span className={`text-xs font-bold ${member.role === 'leader' || member.role === 'co-leader' ? 'text-yellow-500' : member.role === 'officer' ? 'text-cyan-400' : 'text-zinc-400'}`}>{member.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-white text-sm truncate">{member.name}</span>
                          {(member.role === 'leader' || member.role === 'co-leader') && <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-yellow-500/10"><Crown className="w-2.5 h-2.5 text-yellow-500" /><span className="text-yellow-500 text-[10px] font-bold uppercase">Leader</span></div>}
                          {member.role === 'officer' && <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-cyan-400/10"><Shield className="w-2.5 h-2.5 text-cyan-400" /><span className="text-cyan-400 text-[10px] font-bold uppercase">Officer</span></div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Circle className={`w-2.5 h-2.5 ${member.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
                        <span className={`text-xs ${member.online ? 'text-green-500' : 'text-zinc-600'}`}>{member.online ? 'Online' : 'Offline'}</span>
                      {(isClanLeader(selectedClan) || isAdmin) && member.role !== 'leader' && (
                          <>
                            <Select value={member.role} onValueChange={(val) => handleAssignRole(member.id, val)}>
                              <SelectTrigger className="w-[100px] h-7 bg-black border-zinc-800 text-zinc-400 rounded-none text-[10px]" data-testid={`role-select-${member.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border-zinc-800 rounded-none">
                                <SelectItem value="leader" className="text-white text-xs">Leader</SelectItem>
                                <SelectItem value="officer" className="text-white text-xs">Officer</SelectItem>
                                <SelectItem value="member" className="text-white text-xs">Member</SelectItem>
                              </SelectContent>
                            </Select>
                            <button onClick={() => handleRemovePlayer(member.id)} className="text-zinc-600 hover:text-red-500 transition-colors" data-testid={`remove-member-${member.id}`}><UserMinus className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Add Player */}
      <Dialog open={addPlayerOpen} onOpenChange={setAddPlayerOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="add-player-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-white uppercase">Add Player to Clan</DialogTitle></DialogHeader>
          <Input value={playerSearch} onChange={(e) => setPlayerSearch(e.target.value)} placeholder="Search players..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-10 focus:border-cyan-400 mt-4" data-testid="add-player-search" />
          <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
            {filteredAddPlayers.length === 0 ? <p className="text-zinc-500 text-sm text-center py-6">No available players found</p> : filteredAddPlayers.map((p) => (
              <button key={p.id} onClick={() => handleAddPlayer(p.id)} className="w-full flex items-center gap-3 p-3 bg-black border border-zinc-800 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all text-left" data-testid={`add-player-${p.id}`}>
                <div className={`w-8 h-8 flex items-center justify-center ${p.online ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800 border border-zinc-700'}`}><span className={`text-xs font-bold ${p.online ? 'text-green-500' : 'text-zinc-500'}`}>{p.name.charAt(0).toUpperCase()}</span></div>
                <div className="flex-1 min-w-0"><span className="text-white text-sm font-medium">{p.name}</span>{p.team_name ? <span className="text-cyan-400 text-xs ml-2 font-mono">[{p.team_tag}]</span> : <span className="text-zinc-600 text-xs ml-2">No clan</span>}</div>
                <Circle className={`w-2 h-2 ${p.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Clan */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-clan-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-white uppercase">Edit Clan Info</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveInfo} className="space-y-4 mt-4">
            {isAdmin && (<>
              <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400" data-testid="edit-clan-name" /></div>
              <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Tag</Label><Input value={editForm.tag} onChange={(e) => setEditForm({...editForm, tag: e.target.value.slice(0, 10)})} className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400 font-mono" data-testid="edit-clan-tag" /></div>
            </>)}
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} placeholder="Tell us about your clan..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-description-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Discord Invite Link</Label><Input value={editForm.discord} onChange={(e) => setEditForm({...editForm, discord: e.target.value})} placeholder="https://discord.gg/..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400" data-testid="clan-discord-input" /></div>
            <div className="space-y-2"><Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Info / About</Label><Textarea value={editForm.clan_info} onChange={(e) => setEditForm({...editForm, clan_info: e.target.value})} placeholder="Additional info..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-info-input" /></div>
            <Button type="submit" className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="save-clan-info-button">Save Clan Info</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertContent className="bg-[#121212] border-zinc-800 rounded-none">
          <AlertDialogHeader>
            <AlertTitle className="font-heading text-xl text-white uppercase">Remove Clan</AlertTitle>
            <AlertDialogDescription className="text-zinc-400">Are you sure you want to remove <span className="text-white font-bold">[{confirmDelete?.tag}] {confirmDelete?.name}</span>? This will permanently delete the clan and all its data. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-none" data-testid="cancel-delete-clan">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteClan(confirmDelete?.id)} className="bg-red-500 text-white hover:bg-red-600 rounded-none" data-testid="confirm-delete-clan">Yes, Remove Clan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertContent>
      </AlertDialog>
    </main>
  );
}
